import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import {
  CALENDAR_STATUS_KEYS,
  CALENDAR_STATUS_META,
  type CalendarDayStatusCounts,
} from '@/constants/calendarStatus';

dayjs.locale('en');

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

interface StaffScheduleCalendarProps {
  value: string;
  onChange: (value: string) => void;
  /** YYYY-MM-DD → status counts for colored dots */
  dayStatusMap?: Record<string, CalendarDayStatusCounts>;
  /** Fired when the visible month changes (YYYY-MM) */
  onMonthChange?: (month: string) => void;
}

export default function StaffScheduleCalendar({
  value,
  onChange,
  dayStatusMap = {},
  onMonthChange,
}: StaffScheduleCalendarProps) {
  const selected = value && dayjs(value).isValid() ? dayjs(value) : dayjs();
  const [viewMonth, setViewMonth] = useState(() => selected.startOf('month'));
  const today = dayjs().startOf('day');

  useEffect(() => {
    const next = value && dayjs(value).isValid() ? dayjs(value) : dayjs();
    setViewMonth(next.startOf('month'));
  }, [value]);

  useEffect(() => {
    onMonthChange?.(viewMonth.format('YYYY-MM'));
  }, [viewMonth, onMonthChange]);

  const monthCells = useMemo(() => {
    const offset = viewMonth.day();
    const total = viewMonth.daysInMonth();
    const cells: Array<number | null> = [];
    for (let i = 0; i < offset; i += 1) cells.push(null);
    for (let d = 1; d <= total; d += 1) cells.push(d);
    return cells;
  }, [viewMonth]);

  const shiftMonth = (delta: number) => {
    setViewMonth((prev) => prev.add(delta, 'month').startOf('month'));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50"
          aria-label="Previous month"
        >
          <i className="ri-arrow-left-s-line text-gray-600" />
        </button>
        <div className="text-gray-800 text-sm font-semibold">
          {viewMonth.format('MMMM YYYY')}
        </div>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50"
          aria-label="Next month"
        >
          <i className="ri-arrow-right-s-line text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="text-center text-[10px] font-semibold text-gray-400 py-1">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {monthCells.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="h-10" />;
          }

          const iso = viewMonth.date(day).format('YYYY-MM-DD');
          const isSelected = selected.format('YYYY-MM-DD') === iso;
          const isToday = today.format('YYYY-MM-DD') === iso;
          const dayCounts = dayStatusMap[iso];
          const dots = dayCounts
            ? CALENDAR_STATUS_KEYS.filter((key) => (dayCounts[key] ?? 0) > 0)
            : [];

          return (
            <button
              key={iso}
              type="button"
              onClick={() => onChange(iso)}
              className={`h-10 rounded-lg text-sm font-medium transition-colors cursor-pointer flex flex-col items-center justify-center gap-0.5 ${
                isSelected
                  ? 'bg-green-600 text-white'
                  : isToday
                    ? 'text-green-700 bg-green-50 hover:bg-green-100'
                    : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="leading-none">{day}</span>
                  {dots.length > 0 ? (
                <span className="flex items-center justify-center gap-0.5 min-h-[4px]">
                  {dots.slice(0, 5).map((key) => (
                    <span
                      key={key}
                      className={`w-1.5 h-1.5 rounded-full ${CALENDAR_STATUS_META[key].dotClass}${
                        isSelected ? ' ring-1 ring-white/80' : ''
                      }`}
                    />
                  ))}
                </span>
              ) : (
                <span className="min-h-[4px]" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
